// ===========================================
// COSMETICS PREVIEW STORE
// ===========================================
// Manages temporary cosmetic previews at the table
// Preview cosmetics are shown only to the local user
// and are not persisted to the server
//
// Features:
// - 30 second preview timeout
// - Countdown display for last 10 seconds
// - Only one preview at a time (new preview replaces old)

import { create } from 'zustand';

// ===========================================
// CONSTANTS
// ===========================================

const PREVIEW_DURATION_MS = 30 * 1000; // 30 seconds
const COUNTDOWN_THRESHOLD_MS = 10 * 1000; // Show countdown in last 10 seconds
const COUNTDOWN_INTERVAL_MS = 100; // Update every 100ms for smooth countdown

// ===========================================
// TYPES
// ===========================================

export interface PreviewCosmetic {
  id: string;
  category: string;
  name: string;
  priceAmount: number;
  assetUrls: Record<string, string>;
}

interface CosmeticsPreviewState {
  // The currently previewed cosmetic (null = no preview)
  previewCosmetic: PreviewCosmetic | null;

  // Countdown state (milliseconds remaining, null when not in countdown phase)
  timeRemaining: number | null;

  // Whether we're in the countdown phase (last 10 seconds)
  isCountingDown: boolean;

  // Whether the quick shop panel is open
  isQuickShopOpen: boolean;

  // Actions
  setPreview: (cosmetic: PreviewCosmetic | null) => void;
  clearPreview: () => void;
  openQuickShop: () => void;
  closeQuickShop: () => void;
  toggleQuickShop: () => void;
}

// Timer references (outside store to avoid serialization issues)
let previewTimeoutId: ReturnType<typeof setTimeout> | null = null;
let countdownIntervalId: ReturnType<typeof setInterval> | null = null;
let previewEndTime: number | null = null;

// Helper to clear all timers
function clearAllTimers() {
  if (previewTimeoutId) {
    clearTimeout(previewTimeoutId);
    previewTimeoutId = null;
  }
  if (countdownIntervalId) {
    clearInterval(countdownIntervalId);
    countdownIntervalId = null;
  }
  previewEndTime = null;
}

export const useCosmeticsPreviewStore = create<CosmeticsPreviewState>((set, get) => ({
  previewCosmetic: null,
  timeRemaining: null,
  isCountingDown: false,
  isQuickShopOpen: false,

  setPreview: (cosmetic) => {
    // Clear any existing timers
    clearAllTimers();

    if (cosmetic === null) {
      set({ previewCosmetic: null, timeRemaining: null, isCountingDown: false });
      return;
    }

    // Set the new preview
    set({ previewCosmetic: cosmetic, timeRemaining: null, isCountingDown: false });

    // Calculate end time
    previewEndTime = Date.now() + PREVIEW_DURATION_MS;

    // Start countdown interval (checks every 100ms)
    countdownIntervalId = setInterval(() => {
      if (!previewEndTime) return;

      const remaining = previewEndTime - Date.now();

      if (remaining <= 0) {
        // Time's up - clear preview
        clearAllTimers();
        set({ previewCosmetic: null, timeRemaining: null, isCountingDown: false });
      } else if (remaining <= COUNTDOWN_THRESHOLD_MS) {
        // In countdown phase
        set({ timeRemaining: remaining, isCountingDown: true });
      }
    }, COUNTDOWN_INTERVAL_MS);

    // Backup timeout to ensure cleanup (slightly after PREVIEW_DURATION to let interval handle it cleanly)
    previewTimeoutId = setTimeout(() => {
      clearAllTimers();
      set({ previewCosmetic: null, timeRemaining: null, isCountingDown: false });
    }, PREVIEW_DURATION_MS + 200);
  },

  clearPreview: () => {
    clearAllTimers();
    set({ previewCosmetic: null, timeRemaining: null, isCountingDown: false });
  },

  openQuickShop: () => set({ isQuickShopOpen: true }),

  closeQuickShop: () => {
    clearAllTimers();
    set({ isQuickShopOpen: false, previewCosmetic: null, timeRemaining: null, isCountingDown: false });
  },

  toggleQuickShop: () => {
    const isOpen = get().isQuickShopOpen;
    if (isOpen) {
      // Closing - clear preview and timers
      clearAllTimers();
      set({ isQuickShopOpen: false, previewCosmetic: null, timeRemaining: null, isCountingDown: false });
    } else {
      set({ isQuickShopOpen: true });
    }
  },
}));

// ===========================================
// HELPER HOOKS
// ===========================================

/**
 * Get the effective cosmetic for a category.
 * Returns preview cosmetic if one is active for this category,
 * otherwise returns the equipped cosmetic ID.
 */
export function useEffectiveCosmetic(
  category: string,
  equippedCosmeticId: string | null
): { cosmeticId: string | null; isPreview: boolean; assetUrls?: Record<string, string> } {
  const previewCosmetic = useCosmeticsPreviewStore((state) => state.previewCosmetic);

  if (previewCosmetic && previewCosmetic.category === category) {
    return {
      cosmeticId: previewCosmetic.id,
      isPreview: true,
      assetUrls: previewCosmetic.assetUrls,
    };
  }

  return {
    cosmeticId: equippedCosmeticId,
    isPreview: false,
  };
}
